import React, { useEffect, useRef, useState, useContext } from 'react';import { Viewer as ViewerResium, Scene, Primitive, Cesium3DTileset, Globe } from 'resium';
import { Ion, CesiumTerrainProvider } from 'cesium';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';

import Basic2d3dSwitch from 'components/Basic2d3dSwitch';

Ion.defaultAccessToken =
    'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI5YjdmZGI4MC1hNDI2LTQ4NWYtODMyZS1lZGM3NTVlZjMzMDciLCJpZCI6MTE0MDMwLCJpYXQiOjE2NjgzOTg3ODF9.-in98k0Qp44mvZVsxYmNV_Dvk-T9L0gZkenNq9Ex7Qg';

Cesium.GoogleMaps.defaultApiKey = 'AIzaSyAuR77je9nX00QW9LSVR83pD4qjmAj9DuA';
const options = {
    defaultAccessToken:
        'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI5YjdmZGI4MC1hNDI2LTQ4NWYtODMyZS1lZGM3NTVlZjMzMDciLCJpZCI6MTE0MDMwLCJpYXQiOjE2NjgzOTg3ODF9.-in98k0Qp44mvZVsxYmNV_Dvk-T9L0gZkenNq9Ex7Qg'
};
// Terrain provider is created asynchronously (CesiumJS 1.104+ API) inside the component.

function OpenStreetMapNominatimGeocoder() {}

OpenStreetMapNominatimGeocoder.prototype.geocode = function (input) {
    var endpoint = 'https://nominatim.openstreetmap.org/search';
    var resource = new Cesium.Resource({
        url: endpoint,
        queryParameters: {
            format: 'json',
            q: input
        }
    });

    return resource.fetchJson().then(function (results) {
        var bboxDegrees;
        return results.map(function (resultObject) {
            bboxDegrees = resultObject.boundingbox;
            return {
                displayName: resultObject.display_name,
                destination: Cesium.Rectangle.fromDegrees(bboxDegrees[2], bboxDegrees[0], bboxDegrees[3], bboxDegrees[1])
            };
        });
    });
};

function App() {
    const [isLoading, setLoading] = useState(true);
    const [show3dMap, setShow3dMap] = useState(false);
    const [terrainProvider, setTerrainProvider] = useState(undefined);
    const ref = useRef(null);

    useEffect(() => {
        let isMounted = true;
        CesiumTerrainProvider.fromIonAssetId(1, { requestWaterMask: true, requestVertexNormals: true })
            .then((provider) => {
                if (isMounted) {
                    setTerrainProvider(provider);
                }
            })
            .catch((error) => {
                console.error('Failed to load terrain provider:', error);
            });
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (ref.current && ref.current.cesiumElement) {
            let viewer = ref.current.cesiumElement;
            window.viewer = viewer;
            
            // WebGL context loss tracking
            let contextLossCount = 0;
            const MAX_CONTEXT_LOSS = 3;
            let reloadTimeoutId = null;
            
            // Memory monitoring
            const startMemoryMonitoring = () => {
                if (performance.memory) {
                    const memoryInterval = setInterval(() => {
                        if (!viewer || viewer.isDestroyed()) {
                            clearInterval(memoryInterval);
                            return;
                        }
                        
                        const memUsed = performance.memory.usedJSHeapSize;
                        const memLimit = performance.memory.jsHeapSizeLimit;
                        const memPercent = ((memUsed / memLimit) * 100).toFixed(2);
                        
                        console.log(`💾 Memory usage: ${memPercent}% (${(memUsed / 1048576).toFixed(2)} MB / ${(memLimit / 1048576).toFixed(2)} MB)`);
                        
                        if (memPercent > 90) {
                            console.error('⚠️ CRITICAL: Memory usage above 90%! Consider refreshing the page.');
                            if (window.showAlert) {
                                window.showAlert('Memory usage critical! Please save your work and refresh.');
                            }
                        } else if (memPercent > 80) {
                            console.warn('⚠️ WARNING: Memory usage above 80%');
                            if (window.showAlert) {
                                window.showAlert('Memory usage high. Consider removing some 3D models.');
                            }
                        }
                    }, 10000); // Check every 10 seconds
                    
                    return memoryInterval;
                }
                return null;
            };
            
            // Configure Cesium for better stability
            try {
                viewer.scene.requestRenderMode = true;
                viewer.scene.maximumRenderTimeChange = Infinity;
                
                // Disable FXAA for better performance
                viewer.scene.fxaa = false;
                
                // Disable globe lighting for performance
                viewer.scene.globe.enableLighting = false;
                
                console.log('✅ Cesium viewer configured with error handling');
            } catch (error) {
                console.error('❌ Error configuring Cesium viewer:', error);
            }
            
            // Add WebGL context loss/restore handlers
            const canvas = viewer.scene.canvas;
            
            const handleContextLost = (event) => {
                event.preventDefault();
                contextLossCount++;
                console.error(`❌ WebGL context lost (${contextLossCount}/${MAX_CONTEXT_LOSS})`);
                
                if (window.showAlert) {
                    window.showAlert('3D graphics temporarily unavailable. Attempting to recover...');
                }
                
                // Auto-reload after too many context losses (tracked for cleanup)
                if (contextLossCount >= MAX_CONTEXT_LOSS) {
                    console.error('❌ Too many WebGL context losses. Reloading page...');
                    if (window.showAlert) {
                        window.showAlert('Reloading due to graphics errors...');
                    }
                    reloadTimeoutId = setTimeout(() => {
                        // Check viewer is still destroyed before reloading
                        if (!viewer || viewer.isDestroyed()) {
                            window.location.reload();
                        } else {
                            console.log('Viewer recovered, skipping reload');
                        }
                    }, 2000);
                }
            };
            
            const handleContextRestored = () => {
                console.log('✅ WebGL context restored');
                if (window.showAlert) {
                    window.showAlert('3D graphics recovered successfully');
                }
            };
            
            canvas.addEventListener('webglcontextlost', handleContextLost, false);
            canvas.addEventListener('webglcontextrestored', handleContextRestored, false);
            
            // Add Cesium render error handler
            const renderErrorListener = viewer.scene.renderError.addEventListener((scene, error) => {
                console.error('❌ Cesium render error:', error);
                
                // Try to clean up entities on render error
                try {
                    if (window.cleanupOldEntities) {
                        console.log('Attempting entity cleanup after render error...');
                        window.cleanupOldEntities(30); // More aggressive cleanup
                    }
                } catch (cleanupError) {
                    console.error('Error during cleanup:', cleanupError);
                }
                // Do NOT call window.showAlert here — triggers setAlert → parent re-render
                // → useEffect re-runs (was keyed on ref) → TerrainApp remounts → model wiped
            });
            
            // Start memory monitoring
            const memoryInterval = startMemoryMonitoring();

            /*
            window.buildingsTileset = viewer.scene.primitives.add(createOsmBuildings());
            buildingsTileset.style = new Cesium.Cesium3DTileStyle({});

            if (window.viewer.scene.primitives._primitives.length <= 1) {
                Cesium.createGooglePhotorealistic3DTileset(Cesium.GoogleMaps.defaultApiKey, {
                    imageryProvider: false,
                    baseLayerPicker: false,
                    requestRenderMode: true,
                    showCreditsOnScreen: true
                }).then((tileset) => {
                    if (window.viewer.scene.primitives._primitives.length <= 1) {
                        window.viewer.scene.primitives.add(tileset);
                    }
                });
            }
            */

            /*
            buildingsTileset.style = new Cesium.Cesium3DTileStyle({
                color: {
                    conditions: [
                        ["${feature['building']} === 'apartments' || ${feature['building']} === 'residential'", "color('cyan')"],
                        ["${feature['building']} === 'hospital'", "color('rgb(255, 115, 118)')"],
                        ["${feature['building']} === 'commercial'", "color('yellow')"],
                        ["${feature['building']} === 'school'", "color('green')"],
                        [true, "color('white')"]
                    ]
                }
            });
            */
            // viewer._container.firstChild.children[1].style.display = 'none';
            window.viewer = viewer;
            // viewer.geocoder = new OpenStreetMapNominatimGeocoder();

            //Cesium 3d tiles starts here. to be commented when switching to google
            // window.viewer.scene.imageryLayers.add(
            //     new Cesium.ImageryLayer(
            //         new Cesium.OpenStreetMapImageryProvider({
            //             url: 'http://tile.openweathermap.org/map/temp_new',
            //             fileExtension: 'png' + '?area=worldwide&appid=811aec9d921ed18fcb7555a663efa13e'
            //         })
            //     )
            // );
            Cesium.createOsmBuildingsAsync()
                .then((osmBuildings) => {
                    if (viewer && !viewer.isDestroyed()) {
                        viewer.scene.primitives.add(osmBuildings);
                    }
                })
                .catch((error) => {
                    console.error('Failed to load OSM buildings:', error);
                });
            //Cesium 3d tiles

            //Google photo realistic 3d tiles starts here----
            /*
            Cesium.createGooglePhotorealistic3DTileset(Cesium.GoogleMaps.defaultApiKey, {
                imageryProvider: false,
                baseLayerPicker: false,
                requestRenderMode: true,
                showCreditsOnScreen: true
            })
                .then((tileset) => {
                    const googlePhotoRealisticTiles = tileset;
                    window.googlePhotoRealisticTileset = window.viewer.scene.primitives.add(googlePhotoRealisticTiles);
                    window.buildingsTileset = viewer.scene.primitives.add(
                        new Cesium.Cesium3DTileset({
                            url: 'https://tile.googleapis.com/v1/3dtiles/root.json?key=AIzaSyAuR77je9nX00QW9LSVR83pD4qjmAj9DuA',
                            // This property is needed to appropriately display attributions
                            // as required.
                            showCreditsOnScreen: true
                        })
                    );
                })
                .catch((err) => {
                    viewer.scene.primitives.add(Cesium.createOsmBuildings());
                });
            */
            //Google photo realistic 3d tiles ends here----

            setTimeout(() => {
                if (window.runCesiumApp) {
                    try {
                        const result = window.runCesiumApp();
                        if (result && typeof result.catch === 'function') {
                            result.catch((err) => console.warn('runCesiumApp failed:', err));
                        }
                    } catch (err) {
                        console.warn('runCesiumApp failed:', err);
                    }
                    if (show3dMap) {
                        // setTimeout(() => {
                        // document.querySelector('twinworld-app').shadowRoot.querySelectorAll('.cesium-baseLayerPicker-item')[0].click();
                        // window.googlePhotoRealisticTileset = window.viewer.scene.primitives.add(window.googlePhotoRealisticTiles);
                        // window.buildingsTileset = window.viewer.scene.primitives.add(Cesium.createOsmBuildings());
                        // }, 1000);
                        // viewer.scene.mode = Cesium.SceneMode.SCENE3D;
                        if (viewer.scene.camera.position) {
                            viewer.camera.flyTo({
                                destination: viewer.scene.camera.position,
                                orientation: {
                                    heading: Cesium.Math.toRadians(20.0),
                                    pitch: Cesium.Math.toRadians(-35.0),
                                    roll: 0.0
                                }
                            });
                        }
                    } else {
                        // document.querySelector('twinworld-app').shadowRoot.querySelectorAll('.cesium-baseLayerPicker-item')[2].click();
                        setTimeout(() => {
                            // var wms = new Cesium.UrlTemplateImageryProvider({
                            //     url: 'https://api.tomtom.com/traffic/map/4/tile/flow/relative0/{z}/{x}/{y}.png?tileSize=512&key=UQL3Re4IVrz6z90ZoNzDPY1QqHgjso3D',
                            //     rectangle: window.destBoundingBox
                            // });
                            // window.viewer.imageryLayers.addImageryProvider(wms);
                        }, 1500);
                        // Not zooming here
                        // if (viewer.scene.camera.position) {
                        //     console.log("viewer.scene: ", viewer.scene);
                        //     console.log('Camera position for scene click:', viewer.scene.camera.position);
                        //     viewer.camera.flyTo({
                        //         destination: viewer.scene.camera.position,
                        //         orientation: {
                        //             heading: Cesium.Math.toRadians(0.0),
                        //             pitch: Cesium.Math.toRadians(-90.0),
                        //             roll: 0.0
                        //         }
                        //     });
                        // }
                    }
                }
            }, 2000);
            
            // Cleanup function
            return () => {
                console.log('TerrainApp unmounting - cleaning up WebGL listeners');
                
                // Remove event listeners
                if (canvas) {
                    canvas.removeEventListener('webglcontextlost', handleContextLost);
                    canvas.removeEventListener('webglcontextrestored', handleContextRestored);
                }
                
                // Remove render error listener
                if (renderErrorListener) {
                    renderErrorListener();
                }
                
                // Clear memory monitoring
                if (memoryInterval) {
                    clearInterval(memoryInterval);
                }
                
                // Clear reload timeout if pending
                if (reloadTimeoutId) {
                    clearTimeout(reloadTimeoutId);
                }
            };
        }
        // eslint-disable-next-line
        if (!document.querySelector('twinworld-app').shadowRoot.querySelector('.cesium-viewer-geocoderContainer form span.searchBtn')) {
            const btnSpan = document.createElement('span');
            btnSpan.classList.add('searchBtn');
            btnSpan.innerHTML = `<button class="MuiButtonBase-root MuiIconButton-root MuiIconButton-sizeMedium css-1vas6gj-MuiButtonBase-root-MuiIconButton-root" tabindex="0" type="button" aria-label="search" style="
                             transform: translateX(300px) translateY(-40px);">
                             <svg class="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium css-i4bv87-MuiSvgIcon-root" focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="SearchIcon">
                             <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"></path>
                             </svg><span class="MuiTouchRipple-root css-8je8zh-MuiTouchRipple-root"></span></button>`;
            document.querySelector('twinworld-app').shadowRoot.querySelector('.cesium-viewer-geocoderContainer form').append(btnSpan);
        }
    }, [show3dMap]);

    const handleChange = (event) => {
        const show3d = event.target.checked;
        if (show3d) {
            if (viewer.scene.camera.position) {
                viewer.camera.flyTo({
                    destination: viewer.scene.camera.position,
                    orientation: {
                        heading: Cesium.Math.toRadians(20.0),
                        pitch: Cesium.Math.toRadians(-35.0),
                        roll: 0.0
                    }
                });
            }
            //document.querySelector('twinworld-app').shadowRoot.querySelectorAll('.cesium-baseLayerPicker-item')[0].click();
            // try {
            //window.googlePhotoRealisticTileset = window.viewer.scene.primitives.add(window.googlePhotoRealisticTiles);
            // window.buildingsTileset = window.viewer.scene.primitives.add(Cesium.createOsmBuildings());
            // } catch (err) {
            // console.error('3d view');
            // }
        } else {
            if (viewer.scene.camera.position) {
                viewer.camera.flyTo({
                    destination: viewer.scene.camera.position,
                    orientation: {
                        heading: Cesium.Math.toRadians(0.0),
                        pitch: Cesium.Math.toRadians(-90.0),
                        roll: 0.0
                    }
                });
            }
            // if (window.googlePhotoRealisticTileset !== undefined && window.buildingsTileset !== undefined) {
            //     window.viewer.scene.primitives.remove(window.buildingsTileset);
            //     window.viewer.scene.primitives.remove(window.googlePhotoRealisticTileset);
            // }
            // document.querySelector('twinworld-app').shadowRoot.querySelectorAll('.cesium-baseLayerPicker-item')[2].click();
        }
        setShow3dMap(show3d);
        console.log('Change event ', event);
    };

    return (
        <>
            {/* {isLoading ? <img alt="loader" src={https://twinworld-component.s3.amazonaws.com/build/utils/lib/loader.gif'} style={{ height: 'calc(100vh)', width: 'calc(100vw)' }} /> : null}
            <div style={isLoading ? { visibility: 'hidden' } : null}> */}
            <ViewerResium
                // geocode={true}
                // imageryProvider={false}
                baseLayerPicker={true}
                selectionIndicator={false}
                // sceneModePicker={true}
                requestRenderMode={true}
                terrainProvider={terrainProvider}
                ref={ref}
                style={{ height: 'calc(96vh)' }}
            />
            {/* <FormGroup>
                <FormControlLabel
                    id="twoDimThreeDimSwitch"
                    control={<Basic2d3dSwitch checked={show3dMap} onChange={handleChange} />}
                    label="Android 12"
                />
            </FormGroup> */}
            {/* </div> */}
        </>
    );
}

export default App;
